import { useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi.js';
import { fetchPage } from '../lib/api.js';
import { Seo } from '../components/Seo.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { Prose } from '../components/Prose.jsx';
import { NotFoundPage } from './NotFoundPage.jsx';
import { usePage } from '../hooks/usePage.js';

/**
 * Breadcrumbs come from the page row itself — `parentGroup` is the middle
 * crumb label, `parentLink` (optional) makes that crumb clickable. The
 * admin sets both in the Pages resource. Falls back to Home / Page-title
 * when no parent group is configured.
 */
function buildCrumbs(page, title) {
  const home = { label: 'Home', to: '/' };
  const here = { label: title };
  if (page?.parentGroup) {
    const parent = { label: page.parentGroup };
    if (page.parentLink) parent.to = page.parentLink;
    return [home, parent, here];
  }
  return [home, here];
}

export function DynamicPage() {
  const { slug } = useParams();
  // Generic fallback banner for any CMS page without its own heroImage.
  const fallbackBanner = usePage('about').banner;
  const { data: page, loading, error } = useApi(
    () => fetchPage(slug).catch((e) => {
      // 404 is an expected outcome — we fall through to NotFoundPage.
      if (e?.response?.status === 404) return null;
      throw e;
    }),
    [slug],
  );

  if (loading) {
    return (
      <section>
        <div className="container"><p style={{ color: 'var(--ink-3)' }}>Loading…</p></div>
      </section>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader
          crumbs={[{ label: 'Home', to: '/' }, { label: 'Error' }]}
          title="Something went wrong"
          lede="We couldn't load this page. Please try again."
        />
      </>
    );
  }

  if (!page) {
    return <NotFoundPage slug={slug} />;
  }

  const title = page.title || slug;
  const crumbs = buildCrumbs(page, title);

  // Any content page with a portrait image renders the design's `.profile`
  // two-column layout (portrait left, prose right). Without one it's a normal
  // full-width page.
  const portrait = page.portraitImage;
  const usePortraitLayout = !!portrait;
  const isPrincipalsMessage = slug === 'principals-message';

  // Optional gold corner accent on the portrait (matches the leadership accent).
  const portraitFramed = !!portrait && !!page.heroImageFrame;

  // Optional caption (two lines) under the portrait. Admin-managed; the
  // Principal's Message keeps its original copy as a fallback if left blank.
  const hasCaption = !!(page.imageCaptionTitle || page.imageCaptionSubtitle);
  const captionTitle = hasCaption
    ? page.imageCaptionTitle
    : (isPrincipalsMessage ? 'The Principal' : '');
  const captionSubtitle = hasCaption
    ? page.imageCaptionSubtitle
    : (isPrincipalsMessage ? 'Maple Leaf International School' : '');

  return (
    <>
      <Seo
        title={title}
        description={page.metaDescription}
        image={page.heroImage || portrait}
      />

      <PageHeader
        crumbs={crumbs}
        title={title}
        headline={page.headline}
        lede={page.lede}
        image={page.heroImage || fallbackBanner}
      />

      <section>
        <div className="container">
          {usePortraitLayout ? (
            <div className="profile">
              <div>
                <div
                  className={`profile-photo${portraitFramed ? ' with-accent' : ''}`}
                  style={portrait ? { backgroundImage: `url('${portrait}')` } : undefined}
                />
                {(captionTitle || captionSubtitle) && (
                  <span className="caption">
                    {captionTitle && <strong>{captionTitle}</strong>}
                    {captionSubtitle}
                  </span>
                )}
              </div>
              {/* Prose so body extras (e.g. content tabs) work here too. */}
              <Prose html={page.body || undefined} fullWidth>
                <p className="lede">This page is being prepared. Content will be added soon.</p>
              </Prose>
            </div>
          ) : (
            page.body
              ? <Prose html={page.body} />
              : <Prose>
                  <p className="lede">This page is being prepared. Content will be added soon.</p>
                </Prose>
          )}
        </div>
      </section>
    </>
  );
}
